import { useMemo } from "react";
import { Typography } from "@mui/material";
import BaseTable from "@components/DataTable/BaseTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import { Pagination } from "@components/Pagination/Pagination";
import { findCompanyName } from "@features/vessel-call/grid/utils";
import type { ShippingAgencyTransfer } from "@services/shipping-agency-transfer/types";
import type { Company } from "@services/vessel-call/types";
import { t } from "i18next";

const LANG_BASE = "vessel-call.shipping-agency-transfer";
const TEST_ID_BASE = "shipping-agency-previous-transfers";

type PreviousTransfersProps = {
  previousTransfers?: ShippingAgencyTransfer[];
  totalCount?: number;
  pageNumber: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  companies: Company[];
};

const PreviousTransfers: React.FC<PreviousTransfersProps> = ({
  previousTransfers,
  totalCount,
  pageNumber,
  pageSize,
  onPageChange,
  onPageSizeChange,
  companies,
}) => {
  const headers: Header[] = useMemo(
    () => [
      { field: "id", columnName: t(`${LANG_BASE}.id`), minWidth: 80, flex: 1 },
      { field: "ownersMatter", columnName: t(`${LANG_BASE}.owners-matters`) },
      { field: "newShippingAgency", columnName: t(`${LANG_BASE}.new-shipping-agency`) },
      { field: "transferDate", columnName: t(`${LANG_BASE}.transfer-date`) },
    ],
    [],
  );
  const total = totalCount ?? previousTransfers?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const na = t("common.notApplicable");

  const formattedTransfers = useMemo(() => {
    return (
      (previousTransfers &&
        previousTransfers?.map((transfer, index) => ({
          id: index + 1,
          ownersMatter: findCompanyName(companies, transfer?.ownersMatter, na),
          newShippingAgency: transfer?.newShippingAgency.companyNameEnglish || "",
          transferDate: new Date(transfer?.transferDateAndTime).toLocaleDateString(),
        }))) ||
      []
    );
  }, [previousTransfers, companies, na]);

  return (
    <div data-testid={TEST_ID_BASE} className="flex flex-col gap-4">
      <Typography variant="h6" className="text-gray-500">
        {t(`${LANG_BASE}.previous-transfers`)}
      </Typography>
      {formattedTransfers.length > 0 ? (
        <div>
          <BaseTable rows={formattedTransfers} headers={headers} />
          {totalPages > 1 && (
            <div className="flex justify-end">
              <Pagination
                page={pageNumber}
                pageSize={pageSize}
                totalCount={total}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="text-sm font-bold">{t(`${LANG_BASE}.no-previous-transfers`)}</div>
      )}
    </div>
  );
};

export default PreviousTransfers;
